import { useCallback, useEffect, useRef, useState } from "react";

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

export default function BeforeAfter({ before, after, alt, wide = false, autoPlay = true }) {
  const [pos, setPos] = useState(50);
  const [hint, setHint] = useState(false);
  const wrap = useRef(null);
  const drag = useRef(false);
  const userTookOver = useRef(false);
  const raf = useRef(0);
  const hintTimer = useRef(0);

  const move = useCallback((clientX) => {
    const el = wrap.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const next = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(96, Math.max(4, next)));
  }, []);

  const stopAuto = useCallback(() => {
    userTookOver.current = true;
    cancelAnimationFrame(raf.current);
    window.clearTimeout(hintTimer.current);
    setHint(false);
  }, []);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;

    let cancelled = false;
    let started = false;
    let delayId = 0;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const showHint = () => {
      if (cancelled || userTookOver.current) return;
      setHint(true);
      hintTimer.current = window.setTimeout(() => {
        if (!cancelled) setHint(false);
      }, 5000);
    };

    const run = (from, to, duration) =>
      new Promise((resolve) => {
        const start = performance.now();
        const tick = (now) => {
          if (cancelled || userTookOver.current) {
            resolve(false);
            return;
          }
          const t = Math.min(1, (now - start) / duration);
          setPos(from + (to - from) * easeOutCubic(t));
          if (t < 1) raf.current = requestAnimationFrame(tick);
          else resolve(true);
        };
        raf.current = requestAnimationFrame(tick);
      });

    const play = async () => {
      if (reduced) {
        showHint();
        return;
      }
      setPos(22);
      await new Promise((resolve) => {
        delayId = window.setTimeout(resolve, 320);
      });
      if (cancelled || userTookOver.current) return;
      if (!(await run(22, 78, 1700))) return;
      if (!(await run(78, 50, 900))) return;
      showHint();
    };

    if (!autoPlay) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started || cancelled || userTookOver.current) return;
        started = true;
        io.disconnect();
        play();
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    io.observe(el);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf.current);
      window.clearTimeout(hintTimer.current);
      window.clearTimeout(delayId);
      io.disconnect();
    };
  }, [autoPlay]);

  return (
    <div
      ref={wrap}
      className={`ba${wide ? " is-wide" : ""}${hint ? " is-hinting" : ""}`}
      style={{ "--pos": `${pos}%` }}
      role="slider"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos)}
      aria-label={alt || "Vorher-Nachher-Vergleich"}
      tabIndex={0}
      onPointerDown={(e) => {
        stopAuto();
        drag.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        move(e.clientX);
      }}
      onPointerMove={(e) => {
        if (drag.current) move(e.clientX);
      }}
      onPointerUp={() => {
        drag.current = false;
      }}
      onPointerCancel={() => {
        drag.current = false;
      }}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") {
          stopAuto();
          setPos((p) => Math.max(4, p - 4));
        }
        if (e.key === "ArrowRight") {
          stopAuto();
          setPos((p) => Math.min(96, p + 4));
        }
      }}
    >
      <picture>
        {after.webp ? <source srcSet={after.webp} type="image/webp" /> : null}
        <img
          className="ba-photo ba-photo-after"
          src={after.fallback || after.webp}
          alt=""
          draggable={false}
        />
      </picture>
      <picture>
        {before.webp ? <source srcSet={before.webp} type="image/webp" /> : null}
        <img
          className="ba-photo ba-photo-before"
          src={before.fallback || before.webp}
          alt=""
          draggable={false}
        />
      </picture>
      <span className="ba-tag is-before">Vorher</span>
      <span className="ba-tag is-after">Nachher</span>
      {hint ? (
        <span className="ba-hint" aria-hidden="true">
          Ziehen
        </span>
      ) : null}
      <div className="ba-handle" />
    </div>
  );
}
